import {
  getFrontendSentryConfig,
  initFrontendSentry,
} from "@atoms/sentry-module/frontend";
import React, {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import type { ErrorScreen } from "../FrontendProvision";
import { getBackendUrl } from "../getBackendUrl";
import { formatUnknownError } from "../notifications/formatError";
import { useStartupSplashController } from "../StartupSplashController";

// Define the structure of the processed configuration object provided by the context
// This will now store parsed values (e.g., actual booleans instead of "true"/"false" strings)
export interface IFrontendConfigSet {
  [key: string]: string | undefined;
}

const ConfigContext = createContext<IFrontendConfigSet | null>(null);

function buildTracePropagationTargets(
  backendBaseUrl: string,
): Array<string | RegExp> | undefined {
  if (!backendBaseUrl) return undefined;
  const escaped = backendBaseUrl.replaceAll(
    /[.*+?^${}()|[\]\\]/g,
    String.raw`\$&`,
  );
  return [new RegExp(`^${escaped}`)];
}

// Overload for useConfig to allow fetching a specific key with a default value
// or the entire config object if no key is provided.
export function useConfig(): IFrontendConfigSet;
export function useConfig<T = string | boolean>(
  // Adjusted default generic type
  key: string,
  defaultValue?: T,
): T | undefined;
export function useConfig<T = string | boolean>(
  // Adjusted default generic type
  key?: string,
  defaultValue?: T,
): IFrontendConfigSet | T | undefined {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error("useConfig must be used within a ConfigProvider");
  }
  if (key) {
    if (Object.hasOwn(context, key)) {
      return context[key] as T;
    }
    return defaultValue;
  }
  return context;
}

/**
 * Returns the running application version exposed via `/frontend-config`
 * (`APP_VERSION`), or `undefined` when it isn't available (e.g. local dev
 * against an older backend). Backed by the same source of truth as the Sentry
 * release, so it can be shown in the UI and referenced against a changelog.
 */
export function useAppVersion(): string | undefined {
  return useConfig<string>("APP_VERSION");
}

type ConfigProviderProps = Readonly<{
  children: ReactNode;
  splashScreen: ReactNode;
  errorScreen: ErrorScreen;
  sentryRoutePatterns?: readonly string[];
}>;

export function ConfigProvider({
  children,
  splashScreen: _splashScreen,
  errorScreen,
  sentryRoutePatterns,
}: ConfigProviderProps) {
  const [config, setConfig] = useState<IFrontendConfigSet | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { backendBaseUrl } = getBackendUrl();
  const startupSplash = useStartupSplashController();
  const completeStage = startupSplash?.completeStage;
  const finishStartup = startupSplash?.finishStartup;

  useEffect(() => {
    fetch(`${backendBaseUrl}/frontend-config`)
      .then(async (response) => {
        if (!response.ok) {
          const errorBody = await response.text().catch(() => "Unknown error");
          throw new Error(
            `Die Frontend-Konfiguration konnte nicht abgerufen werden. Status: ${response.status}. Details: ${errorBody}`,
          );
        }
        return response.json() as Promise<IFrontendConfigSet>; // Use IFrontendConfigSet here
      })
      .then((data: IFrontendConfigSet) => {
        // Assuming the actual key-value pairs are in data.entries
        // And that data.entries is Record<string, string>
        // biome-ignore lint/suspicious/noExplicitAny: legacy code
        (globalThis as any).config = data;
        // Sentry is optional telemetry — fire-and-forget so a slow/stuck dynamic
        // import of the SDK can't keep the app on the loading screen. Use a
        // start-anchored RegExp instead of a raw string: Sentry substring-matches
        // string entries, so a relative base like "/" or "/api" would also match
        // third-party URLs and leak sentry-trace/baggage headers to them.
        initFrontendSentry(getFrontendSentryConfig(data), {
          tracePropagationTargets: buildTracePropagationTargets(backendBaseUrl),
          routePatterns: sentryRoutePatterns,
        }).catch((sentryError) => {
          console.error("Failed to initialize Sentry SDK", sentryError);
        });
        setConfig(data);
        setIsLoading(false);
        completeStage?.("config");
      })
      .catch((error_) => {
        let errorToSet: Error;
        if (error_ instanceof Error) {
          errorToSet = error_;
        } else {
          errorToSet = new Error(String(error_));
        }

        if (
          errorToSet.message.startsWith("Failed to fetch") ||
          errorToSet.message.includes("NetworkError")
        ) {
          errorToSet.message =
            "Leider ist der Server nicht erreichbar. Bitte versuchen Sie es später erneut. Code: ERR_FRONTEND_CONFIG";
        }

        setError(errorToSet);
        setIsLoading(false);
        completeStage?.("config");
        finishStartup?.();
      });
  }, [backendBaseUrl, completeStage, finishStartup, sentryRoutePatterns]);

  if (error) {
    if (typeof errorScreen === "function") {
      return errorScreen({ error: error });
    }
    if (React.isValidElement(errorScreen)) {
      return React.cloneElement(
        errorScreen as React.ReactElement<{ error?: Error }>,
        { error: error },
      );
    }
    const formatted = formatUnknownError(error, {
      defaultMessage: "Die Frontend-Konfiguration konnte nicht geladen werden.",
      keyPrefix: "frontend-config",
    });
    return (
      <div>
        <p>{formatted.message}</p>
        {formatted.description ? <p>{formatted.description}</p> : null}
        <button type="button" onClick={() => globalThis.location.reload()}>
          Erneut versuchen
        </button>
      </div>
    );
  }
  if (isLoading || !config) {
    return null;
  }

  return (
    <ConfigContext.Provider value={config}>{children}</ConfigContext.Provider>
  );
}
